import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/help/workspace-access")({
	component: WorkspaceAccess,
});

export function WorkspaceAccess() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Workspace Guide</p>
				<h1
					className="text-4xl font-semibold tracking-tight"
					style={{
						color: "var(--km-on-surface)",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Workspace access
				</h1>
				<p className="text-sm" style={{ color: "var(--km-on-surface-muted)" }}>
					How workspace membership, active context, and MCP access fit together.
				</p>
			</div>

			<div
				className="space-y-4 text-sm leading-relaxed"
				style={{ color: "var(--km-on-surface-muted)" }}
			>
				<p>
					Every MCP session runs against one active workspace. That workspace
					decides which generations, balance, members, and settings the
					assistant can access.
				</p>
				<p>
					If your account belongs to multiple workspaces, the assistant should
					list or switch workspaces before taking an action that affects
					billing, team members, or stored generations.
				</p>

				<h2
					className="text-base font-semibold pt-2"
					style={{
						color: "var(--km-on-surface)",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Access rules
				</h2>
				<div className="space-y-2">
					{[
						"Only members of a workspace can view its generations, balance, and team state.",
						"Owners can manage billing, settings, invitations, and destructive workspace actions.",
						"Members can run supported day-to-day actions inside the workspace but cannot change ownership-level settings.",
					].map((rule) => (
						<div key={rule} className="flex items-start gap-3 px-4 py-3" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderLeft: "3px solid #6B3FF2" }}>
							<span className="material-symbols-outlined mt-0.5 shrink-0" aria-hidden="true" style={{ fontSize: "14px", color: "#6B3FF2" }}>check_circle</span>
							<p className="text-xs leading-relaxed">{rule}</p>
						</div>
					))}
				</div>

				<h2
					className="text-base font-semibold pt-2"
					style={{
						color: "var(--km-on-surface)",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Role capabilities
				</h2>
			</div>

			{/* Role matrix */}
			<table
				style={{ border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2", width: "100%", borderCollapse: "collapse" }}
				aria-label="Role capabilities"
			>
				<thead>
					<tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
						<th scope="col" className="px-4 py-2.5 text-left">
							<span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ fontFamily: "inherit", color: "#6B3FF2" }}>Capability</span>
						</th>
						<th scope="col" className="px-6 py-2.5 text-center" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
							<span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ fontFamily: "inherit", color: "#6B3FF2" }}>Owner</span>
						</th>
						<th scope="col" className="px-6 py-2.5 text-center" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
							<span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ fontFamily: "inherit", color: "#6B3FF2" }}>Member</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{[
						{ label: "Generate images, video, audio", owner: true, member: true },
						{ label: "View workspace generations", owner: true, member: true },
						{ label: "Check balance", owner: true, member: true },
						{ label: "Invite team members", owner: true, member: false },
						{ label: "Top up workspace balance", owner: true, member: false },
						{ label: "Rename or delete workspace", owner: true, member: false },
						{ label: "Create / revoke API keys", owner: true, member: false },
					].map((row, i) => (
						<tr
							key={row.label}
							style={{ borderTop: i === 0 ? undefined : "1px solid rgba(255,255,255,0.08)", backgroundColor: i % 2 === 0 ? "var(--km-surface)" : "var(--km-surface-low)" }}
						>
							<td className="px-4 py-3">
								<span className="text-xs" style={{ color: "var(--km-on-surface-muted)" }}>{row.label}</span>
							</td>
							<td className="px-6 py-3 text-center" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
								{row.owner
									? <><span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}>check_circle</span><span className="sr-only">Yes</span></>
									: <><span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "var(--km-on-surface-muted)" }}>remove</span><span className="sr-only">No</span></>
								}
							</td>
							<td className="px-6 py-3 text-center" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
								{row.member
									? <><span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}>check_circle</span><span className="sr-only">Yes</span></>
									: <><span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "var(--km-on-surface-muted)" }}>remove</span><span className="sr-only">No</span></>
								}
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<div
				className="space-y-4 text-sm leading-relaxed"
				style={{ color: "var(--km-on-surface-muted)" }}
			>
				<h2
					className="text-base font-semibold pt-2"
					style={{
						color: "var(--km-on-surface)",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Recommended assistant flow
				</h2>
				<p>
					When a user says "use the Acme workspace" or "invite someone to
					marketing," the assistant should first confirm or switch the active
					workspace, then run the relevant team or generation tool inside that
					context.
				</p>
			</div>

			<div
				className="flex items-center justify-between text-sm pt-4"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				<Link
					to="/help/roles"
					className="flex items-center gap-1.5 font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)", textDecoration: "none" }}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
					onFocus={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onBlur={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
					Roles and permissions
				</Link>
				<Link
					to="/help/mcp"
					className="flex items-center gap-1.5 transition-colors font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "#6B3FF2", textDecoration: "none" }}
					onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					onFocus={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onBlur={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
				>
					MCP overview
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
				</Link>
			</div>
		</article>
	);
}
